import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const PALETTE = ["#06b6d4", "#f59e0b", "#6366f1", "#ef4444", "#8b5cf6", "#10b981"];

const METRIC_LABELS = {
  commits: "Commits",
  issues_opened: "Issues Opened",
  issues_closed: "Issues Closed",
  prs_opened: "PRs Opened",
  prs_merged: "PRs Merged",
  prs_closed: "PRs Closed",
};

const PR_METRICS = ["prs_opened", "prs_merged", "prs_closed"];
const ISSUE_METRICS = ["issues_opened", "issues_closed"];
const COMMIT_METRICS = ["commits"];

// ---------- helpers ----------
const toSeriesRows = (weekly, metrics) => {
  if (!weekly?.timeseries?.days?.length) return [];
  return weekly.timeseries.days.map((ymd, i) => {
    const row = { date: ymd, x: ymd };
    metrics.forEach((m) => {
      row[m] = weekly.timeseries[m]?.[i] ?? 0;
    });
    return row;
  });
};

const parseLocalYMD = (ymd) => {
  if (typeof ymd !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDate = (dateStr) => {
  const dt = parseLocalYMD(dateStr);
  if (!dt) return dateStr;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatNumberShort = (v) => {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "K";
  return String(v);
};

const RANGE_DAY_COUNTS = { "7d": 7, "1m": 30, "3m": 90, "6m": 180, "1y": 365 };

const pad2 = (n) => String(n).padStart(2, "0");
const toYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const daysBackList = (n) => {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(toYMD(d));
  }
  return out;
};

const buildSkeletonWeekly = (rangeKey = "7d") => {
  const n = RANGE_DAY_COUNTS[rangeKey] ?? 7;
  const days = daysBackList(n);
  const zeros = Array(n).fill(0);
  return {
    timeseries: {
      days,
      commits: zeros.slice(),
      issues_opened: zeros.slice(),
      issues_closed: zeros.slice(),
      prs_opened: zeros.slice(),
      prs_merged: zeros.slice(),
      prs_closed: zeros.slice(),
    },
  };
};

// ---------- UI bits ----------
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e6ed",
        borderRadius: 8,
        padding: 12,
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        minWidth: 200,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 6 }}>
        {formatDate(label)}
      </div>
      {payload
        .slice()
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
        .map((p, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", margin: "2px 0", fontSize: 13 }}>
            <span style={{ color: p.color, fontWeight: 600 }}>{p.name}</span>
            <span style={{ fontWeight: 700, color: "#111827" }}>{p.value}</span>
          </div>
        ))}
    </div>
  );
};

const CustomXAxisTick = ({ x, y, payload, onClick, selected }) => {
  const isSelected = selected === payload.value;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={16}
        textAnchor="middle"
        fill={isSelected ? "#2563eb" : "#666"}
        fontSize={11}
        style={{ cursor: onClick ? "pointer" : "default", fontWeight: isSelected ? 700 : 400 }}
        onClick={() => onClick?.(payload.value)}
      >
        {formatDate(payload.value)}
      </text>
    </g>
  );
};

function SectionChart({
  title,
  metrics,
  data,
  colors,
  syncId,
  syncHover = false,
  selectedDate,
  setSelectedDate,
  onDateClick,
  height,
  sectionKey,
  showLegend = true,
}) {
  return (
    <div className="card p-3 shadow-sm h-100 mt-3">
      <h6 className="mb-2">{title}</h6>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={data}
          margin={{ top: 12, right: 24, left: 8, bottom: 50 }}
          syncId={syncHover ? syncId : undefined}
          onClick={(evt) => {
            if (evt?.activeLabel) {
              setSelectedDate(evt.activeLabel);
              onDateClick?.(evt.activeLabel);
            }
          }}
        >
          <defs>
            {metrics.map((m, i) => {
              const c = colors[i] || PALETTE[i % PALETTE.length];
              return (
                <linearGradient key={`grad-${sectionKey}-${m}`} id={`grad-${sectionKey}-${m}`} x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={c} stopOpacity={0.8} />
                  <stop offset="50%" stopColor={c} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={c} stopOpacity={0.1} />
                </linearGradient>
              );
            })}
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" strokeOpacity={0.6} />

          <XAxis
            dataKey="x"
            height={50}
            tickCount={data.length > 120 ? 8 : data.length > 60 ? 7 : 8}
            tick={
              <CustomXAxisTick
                onClick={(v) => {
                  setSelectedDate(v);
                  onDateClick?.(v);
                }}
                selected={selectedDate}
              />
            }
            axisLine={{ stroke: "#d0d0d0" }}
            tickLine={{ stroke: "#d0d0d0" }}
          />

          <YAxis
            tick={{ fontSize: 11, fill: "#666" }}
            tickFormatter={formatNumberShort}
            width={70}
            axisLine={{ stroke: "#d0d0d0" }}
            tickLine={{ stroke: "#d0d0d0" }}
            allowDecimals={false}
          />

          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend wrapperStyle={{ paddingTop: 12, fontSize: 12 }} iconType="rect" />}

          {metrics.map((m, i) => {
            const c = colors[i] || PALETTE[i % PALETTE.length];
            return (
              <Area
                key={`${sectionKey}-${m}`}
                type="monotone"
                dataKey={m}
                name={METRIC_LABELS[m] || m}
                stroke={c}
                strokeWidth={data.length > 100 ? 1.5 : 2.5}
                fill={`url(#grad-${sectionKey}-${m})`}
                fillOpacity={0.6}
                activeDot={{ r: 7, stroke: c, strokeWidth: 3, fill: "#fff" }}
                dot={data.length <= 40}
                connectNulls
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Main component ----------
/**
 * Props:
 * - owner (required)
 * - repo  (required)
 * - apiBase (default "/github")
 * - weekly / datasets (optional seed)
 * - colors / heights
 * - onDateClick(dateStr)
 * - extraHeaders?  <-- new: pass { "X-API-Key": "...", ... } if your API needs it
 * - withCredentials? boolean (default true)
 */
export default function WeeklyTrendAreaCharts({
  owner,
  repo,
  apiBase = "/github",
  weekly,
  project_id,
  datasets,
  colors = [],
  heights = { prs: 260, issues: 260, commits: 220 },
  onDateClick,
  extraHeaders = {},
  withCredentials = true,
}) {
  const RANGE_OPTIONS = [
    { key: "7d", label: "Last 7 days" },
    { key: "1m", label: "Last 1 month" },
    { key: "3m", label: "Last 3 months" },
    { key: "6m", label: "Last 6 months" },
    { key: "1y", label: "Last 1 year" },
  ];

  const [selectedRange, setSelectedRange] = useState("7d");
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rangeError, setRangeError] = useState("");

  const [dataByRange, setDataByRange] = useState(() => {
    const seed = {};
    if (weekly) seed["7d"] = weekly;
    if (datasets && typeof datasets === "object") {
      Object.entries(datasets).forEach(([k, v]) => {
        if (v) seed[k] = v;
      });
    }
    return seed;
  });

  const fetchFromAPI = useCallback(
    async (rangeKey, signal) => {
      if (!owner || !repo) throw new Error("owner/repo are required to fetch");
      const url = `${apiBase.replace(/\/$/, "")}/repos/${owner}/${repo}/${project_id}/analytics/weekly/?range=${encodeURIComponent(rangeKey)}`;

      // IMPORTANT: include credentials/headers to avoid HTML responses that break JSON.parse
      const res = await fetch(url, {
        method: "GET",
        signal,
        credentials: withCredentials ? "include" : "same-origin",
        headers: {
          "Accept": "application/json",
          ...extraHeaders,
        },
      });

      if (!res.ok) {
        // Try to read text to surface server error body
        const txt = await res.text().catch(() => "");
        throw new Error(`API ${res.status}: ${txt || "Failed to fetch"}`);
      }

      const json = await res.json();
      if (!json?.timeseries?.days) return buildSkeletonWeekly(rangeKey);
      return { timeseries: json.timeseries };
    },
    [apiBase, owner, repo, extraHeaders, withCredentials]
  );

  const ensureRangeLoaded = useCallback(
    async (rangeKey) => {
      if (dataByRange[rangeKey]) return;
      const ctrl = new AbortController();
      try {
        setLoading(true);
        setRangeError("");
        const result = await fetchFromAPI(rangeKey, ctrl.signal);
        setDataByRange((prev) => ({ ...prev, [rangeKey]: result }));
      } catch (e) {
        console.error(e);
        setRangeError(e?.message || "Failed to load data for the selected range.");
        setDataByRange((prev) => ({ ...prev, [rangeKey]: buildSkeletonWeekly(rangeKey) }));
      } finally {
        setLoading(false);
      }
      return () => ctrl.abort();
    },
    [dataByRange, fetchFromAPI]
  );

  useEffect(() => {
    if (!dataByRange["7d"]) {
      ensureRangeLoaded("7d");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, repo, apiBase]);

  useEffect(() => {
    ensureRangeLoaded(selectedRange);
  }, [selectedRange, ensureRangeLoaded]);

  const baseWeekly = dataByRange[selectedRange];
  const hasDays = baseWeekly?.timeseries?.days?.length > 0;
  const currentWeekly = hasDays ? baseWeekly : buildSkeletonWeekly(selectedRange);

  const prsData = useMemo(() => toSeriesRows(currentWeekly, PR_METRICS), [currentWeekly]);
  const issueData = useMemo(() => toSeriesRows(currentWeekly, ISSUE_METRICS), [currentWeekly]);
  const commitData = useMemo(() => toSeriesRows(currentWeekly, COMMIT_METRICS), [currentWeekly]);

  const totalDays = currentWeekly?.timeseries?.days?.length ?? 0;

  return (
    <div>
      <div className="card p-3 shadow-sm h-100 mt-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
          <h6 className="mb-0">Activity Trend</h6>
          <div className="d-flex align-items-center gap-2">
            {totalDays > 0 && <small className="text-muted me-2">Showing {totalDays} day(s)</small>}
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 170 }}
              value={selectedRange}
              onChange={(e) => {
                setSelectedDate(null);
                setSelectedRange(e.target.value);
              }}
            >
              {RANGE_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && <div className="text-center text-muted py-3">Loading selected range…</div>}
        {rangeError && <div className="alert alert-warning py-2">{rangeError}</div>}

        <div className="row">
          <div className="col-md-6">
            <SectionChart
              title="Pull Requests: Opened • Merged • Closed"
              metrics={PR_METRICS}
              data={prsData}
              colors={colors}
              sectionKey="prs"
              syncId="weekly-split-sync"
              syncHover={false}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onDateClick={onDateClick}
              height={heights.prs}
              showLegend
            />
          </div>

          <div className="col-md-6">
            <SectionChart
              title="Issues: Opened • Closed"
              metrics={ISSUE_METRICS}
              data={issueData}
              colors={colors}
              sectionKey="issues"
              syncId="weekly-split-sync"
              syncHover={false}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onDateClick={onDateClick}
              height={heights.issues}
              showLegend
            />
          </div>
        </div>

        <div className="row mt-3 mb-3">
          <div className="col-12">
            <SectionChart
              title="Commits"
              metrics={COMMIT_METRICS}
              data={commitData}
              colors={colors}
              sectionKey="commits"
              syncId="weekly-split-sync"
              syncHover={false}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onDateClick={onDateClick}
              height={heights.commits}
              showLegend={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
